import {
	ShareBaseFolder,
	ShareBaseDocument,
	FolderShare,
	ObjectType,
	ReceivedShareInfo
} from 'phoenix/domain';

import {
	IShareableService,
	ReceivedLinkShare,
	ServerLinkShare,
	CreatedLinkShare,
	LinkShare,
	RightsRestrictions
} from 'sharebase/domain';

import { PatchCommand } from 'util/domain';

export interface IFolderService extends IShareableService {
	createFolder(name:string, parentFolder:ShareBaseFolder): ng.IPromise<ShareBaseFolder>;
	renameFolder(folder:ShareBaseFolder, newName:string): ng.IPromise<ShareBaseFolder>;
	deleteFolder(folder:ShareBaseFolder, purge?:boolean): ng.IPromise<void>;
	moveFolder(source:ShareBaseFolder, destination:ShareBaseFolder): ng.IPromise<{}>;
	getFolder(id:number): ng.IPromise<ShareBaseFolder>;
	getRootFolders(): ng.IPromise<ShareBaseFolder[]>;
	getSubFolders(folder:ShareBaseFolder): ng.IPromise<ShareBaseFolder[]>;
	getDocuments(folder:ShareBaseFolder): ng.IPromise<ShareBaseDocument[]>;
	hasSubFolders(folder:ShareBaseFolder): ng.IPromise<boolean>;
	folderNameExists(folder:ShareBaseFolder, name:string): ng.IPromise<boolean>;
	updateShaasFolderShare(shareId:number, folderPatch: any): any;
}


/**
* @ngdoc service
* @name  sharebase.sbFolderService
* 
* @description 
* A service used to retrieve and interact with ShareBase
* folders
**/

export function FolderServiceFactory(
	$http:ng.IHttpService, 
	pxUriPathService, 
	settingsService, 
	sbLibraryService
) {
	const service:IFolderService = {
		getRights,
		createLink,
		revokeLink,
		getShares,
		getLinks,
		getLinkRights,
		getPermissionsForCurrentUser,
		/**
		* @ngdoc method
		* @name sbFolderService#getRootFolders
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Gets the folders available to the user
		* 
		* @returns {Deferred} A promise that will resolve with an
		* array of folders
		* 
		*/
		getRootFolders,

		/**
		* @ngdoc method
		* @name sbFolderService#getFolder
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Gets a specific folder to the user
		* 
		* @param {object} folderId A Folder ID
		*
		* @returns {Deferred} A promise that will resolve with a folder
		* 
		*/
		getFolder,

		/**
		* @ngdoc method
		* @name bFolderService#getSubFolders
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Gets the contents of a given folder
		* 
		* @param {object} folder A folder resource to get sub-folders for
		*
		* @returns {Deferred} A promise that will resolve with an
		* array of subFolders
		* 
		*/
		getSubFolders,

		/**
		* @ngdoc method
		* @name bFolderService#getDocuments
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Gets the contents of a given folder
		* 
		* @param {object} folder A folder resource to get documents for
		*
		* @returns {Deferred} A promise that will resolve with an
		* array of documents
		* 
		*/
		getDocuments,

		/**
		* @ngdoc method
		* @name bFolderService#hasSubFolders
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Calculates whether a given folder has subFolders
		*
		* @param {object} folder A folder resource to check for children
		* 
		* @returns {Deferred} A promise that will resolve with an
		* boolean indicator as to whether a given folder contains subFolders
		* 
		*/
		hasSubFolders,

		/**
		* @ngdoc method
		* @name bFolderService#folderNameExists
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Checks to see if a folder's name already exists in the current directory
		* 
		* @param {object} folder A folder resource whose children to check for a 
		* given name
		* @param {string} folderName A name to check for
		*
		* @returns {Deferred} A boolean value determining if the folder's name 
		* already exists
		* in the current directory.
		* 
		*/
		folderNameExists,

		/**
		* @ngdoc method
		* @name bFolderService#createFolder
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Creates a new folder
		*
		* @param {string} folderName The name of the new folder
		* @param {object} parentFolder The new folders parent
		* 
		* @returns {Deferred} A promise that will resolve with a created folder
		* 
		*/
		createFolder,

		/**
		* @ngdoc method
		* @name bFolderService#renameFolder
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Renames an existing folder
		* 
		* @param {object} folder A folder resource
		* @param {string} newName The new name
		*
		* @returns {Deferred} A promise that will resolve with a renamed folder
		* 
		*/
		renameFolder,

		/**
		* @ngdoc method
		* @name bFolderService#moveFolder
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Moves an existing folder
		* 
		* @param {object} source A folder resource to move
		* @param {object} destination A folder or library resource to
		* move the folder to
		*/
		moveFolder,

		/**
		* @ngdoc method
		* @name bFolderService#deleteFolder
		* @methodOf sharebase.sbFolderService
		* @kind function
		*
		* @description
		* Deletes an existing folder
		* 
		* @param {object} folder A folder resource
		*/
		deleteFolder,
		createShare,
		updateShare,
		updateShaasFolderShare,
		deleteShare,
		requestPermissions: (folder:ShareBaseFolder, perms) => {
			let uri = `${folder.Links.Self}/acls/requests`;
			return $http.post(uri, perms);
			}
	};

	return service;

	function getRootFolders() {
		return sbLibraryService.getUserLibrary();
	}

	function getFolder(folderId: number) {
		return $http.get(settingsService.serverAPIPath + '/f/' + folderId + '/?ip=true')
			.then(function (response) {
				return response.data as ShareBaseFolder;
			});
	}

	function getSubFolders(folder) {
		return $http.get(folder.Links.ChildFoldersUri)
			.then(function (response) {
				return response.data as ShareBaseFolder[];
			});
	}

	function getDocuments(folder) {
		return $http.get(folder.Links.DocumentsUri)
			.then(function (response) {
				return response.data as ShareBaseDocument[];
			});
	}

	function hasSubFolders(folder) {
		return $http.get(folder.Links.ChildFoldersUri)
			.then(function (response) {
				return angular.isDefined(response.data);
			});
	}

	function folderNameExists(folder, folderName) {
		return $http.get(folder.Links.ChildFoldersUri)
			.then(function (response) {
				var folders = response.data as ShareBaseFolder[];

				return folders.some(function (child) {
					return child.FolderName === folderName;
				});
			});
	}

	function createFolder(folderName, parentFolder) {
		var apiPath = parentFolder ?
			parentFolder.Links.ChildFoldersUri :
			settingsService.serverAPIPath + '/f';

		return $http
			.post(apiPath, {FolderName:folderName})
			.then(function (response) {
				return response.data as ShareBaseFolder;
			});
	}

	function renameFolder(folder, newName) {
		var renameJson = JSON.stringify({ FolderName: newName });

		return $http.patch(folder.Links.Self, renameJson)
			.then(function (response) {
				return response.data as ShareBaseFolder;
			});
	}

	function moveFolder(source, destination) {
		var moveJson = JSON.stringify({ FolderId: source.FolderId });
		var url = destination.IsLibrary ? destination.Links.RootFoldersUri : destination.Links.ChildFoldersUri;
		return $http.patch(url, moveJson);
	}

	function deleteFolder(folder: ShareBaseFolder, purge:boolean = false) {
		let id = folder.FolderId;
		let uri = purge ? '/async/f/{id}?purge=true' : '/f/{id}?purge=false';
		uri = pxUriPathService.resolvePath(uri, {
			id
		});
		
		return $http.delete(uri).then(() => undefined);
	}

	function getShares(folder:ShareBaseFolder, compact:boolean = true){
		let uri = folder.Links.Self + '/permissions';

		if (compact) {
			uri += '?compact=true';
		}

		return $http
			.get(uri)
			.then(response => response.data as ReceivedShareInfo[]);
	}

	function getRights(currentUserBits:number, targetUserBits:number = 0, isInherited:boolean = false){
		var uri = pxUriPathService.resolvePath('/rights-and-restrictions/shareWithUser-v2?acl={acl}&targetAcl={targetAcl}&objType={type}&isInherited={inherited}', {
			acl : currentUserBits,
			type: ObjectType.FOLDER,
			targetAcl: targetUserBits,
			inherited: isInherited
		});

		return $http.get(uri).then(resp => resp.data as RightsRestrictions);
	}

	function createLink(folder:ShareBaseFolder, share: ServerLinkShare) {
		let uri = folder.Links.SharesUri;

		return $http
			.post(uri, share)
			.then(resp => resp.data as CreatedLinkShare);
	}

	function revokeLink(revokedLink:LinkShare){
		return $http
			.post(revokedLink.Links.RevokeUri,{})
			.then(resp => resp.data as CreatedLinkShare);
	}

	function getLinks(folder) {
		let uri = folder.Links.SharesUri + '/all';

		return $http.get(uri).then(resp => resp.data as ReceivedLinkShare[]);
	}	

	function getLinkRights(rightBits:number){
		let uri = pxUriPathService.resolvePath('/rights-and-restrictions/shareAsLink-v2?acl={acl}&objType={type}', {
			acl: rightBits,
			type: ObjectType.FOLDER
		});

		return $http.get(uri).then(resp => resp.data as RightsRestrictions);
	}

	function getPermissionsForCurrentUser(folder) {
		let uri = folder.Links.Self + '/permissionsforuser';

		return $http
			.get(uri)
			.then(response => response.data as ReceivedShareInfo[]);
	}

	function createShare(folder:ShareBaseFolder, share:FolderShare){
		let uri = `${folder.Links.Self}/acls`;
		return $http.post(uri, share);
	}

	function updateShare(folder:ShareBaseFolder, share:FolderShare){
		let uri = `${folder.Links.Self}/acls/${share.FolderACLId}`;
		return $http.patch(uri, share);
	}

	function updateShaasFolderShare(shareId:number, folderPatch:PatchCommand[]){
		let uri = pxUriPathService.resolvePath( `/f/shares/${shareId}`);
		return $http.patch(uri, folderPatch);
	}

	function deleteShare(folder:ShareBaseFolder, share:FolderShare){
		let uri = `${folder.Links.Self}/acls/${share.FolderACLId}`;
		return $http.delete(uri);
	}
}

FolderServiceFactory.$inject = [
	'$http',
	'pxUriPathService',
	'settingsService',
	'sbLibraryService'
];


// WEBPACK FOOTER //
// ./src/sharebase/services/folderService.ts